/**
 * WellnessTab — Careflow windows, check-in windows, and rounds windows.
 *
 * Careflow filtering by escalation_type_id:
 *   Type 1 (Default): always shown
 *   Type 4 (Resident Group): show if references overlap resGroups
 *   Type 5 (Area Group): show if references overlap areaGroups
 *   Display order: Resident → Area → Default
 *
 * Check-in windows: window_type_id 1, filtered by resident_group_id_list
 * Rounds windows: window_type_id 2, filtered by area_group_id_list
 *
 * Times: HHMMtoLocalTime(convertSectoHHMM(seconds))
 * Read-only display.
 */

import { useMemo } from 'react';
import { convertSectoHHMM, HHMMtoLocalTime } from '../../../../../libs.jsx';

const WellnessTab = ({
  careData, wellnessWindowData, resWellData,
  resGroups, areaGroups,
}) => {
  /** Format seconds → local time string */
  const formatTime = (seconds) => HHMMtoLocalTime(convertSectoHHMM(seconds));

  /** Resident group IDs set */
  const resGroupIds = useMemo(() => {
    return new Set((resGroups || []).map((g) => g.resident_group_id?.toString()));
  }, [resGroups]);

  /** Area group IDs set */
  const areaGroupIds = useMemo(() => {
    return new Set((areaGroups || []).map((g) => g.area_group_id?.toString()));
  }, [areaGroups]);

  /** Careflow windows — Resident (4) → Area (5) → Default (1) */
  const careflowWindows = useMemo(() => {
    const resFlows = [];
    const areaFlows = [];
    const defaultFlows = [];

    (careData || []).forEach((cf) => {
      if (cf.escalation_type_id === 1) {
        defaultFlows.push({ ...cf, typeLabel: 'Default' });
      } else if (cf.escalation_type_id === 4) {
        const refs = (cf.references || '').split(',');
        if (refs.some((r) => resGroupIds.has(r))) {
          resFlows.push({ ...cf, typeLabel: 'Resident' });
        }
      } else if (cf.escalation_type_id === 5) {
        const refs = (cf.references || '').split(',');
        if (refs.some((r) => areaGroupIds.has(r))) {
          areaFlows.push({ ...cf, typeLabel: 'Area' });
        }
      }
    });

    return [...resFlows, ...areaFlows, ...defaultFlows];
  }, [careData, resGroupIds, areaGroupIds]);

  /** Check-in windows — window_type_id 1, filtered by resident_group_id_list */
  const checkinWindows = useMemo(() => {
    return (wellnessWindowData || []).filter((w) => {
      if (w.window_type_id !== 1) return false;
      const groupList = (w.resident_group_id_list || '').split(',');
      return groupList.some((id) => resGroupIds.has(id));
    });
  }, [wellnessWindowData, resGroupIds]);

  /** Rounds windows — window_type_id 2, filtered by area_group_id_list */
  const roundsWindows = useMemo(() => {
    return (wellnessWindowData || []).filter((w) => {
      if (w.window_type_id !== 2) return false;
      const groupList = (w.area_group_id_list || '').split(',');
      return groupList.some((id) => areaGroupIds.has(id));
    });
  }, [wellnessWindowData, areaGroupIds]);

  return (
    <>
      {/* Careflow */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">
            Careflow Windows
            <span className={"pp-section__count"}>({careflowWindows?.length})</span>
          </h3>
        </div>
        <div className="pp-section__body">
          {careflowWindows.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {careflowWindows.map((cf, idx) => (
                <div key={`cf-${idx}`} className="rpp-careflow-item">
                  <span className="rpp-careflow-name" title={cf.careflow_description}>
                    {cf.careflow_description}
                  </span>
                  <span className="rpp-careflow-time">
                    {formatTime(cf.enable_start)} &ndash; {formatTime(cf.enable_end)}
                  </span>
                  <span className="rpp-careflow-type">{cf.typeLabel}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="dp-cell-text dp-cell-text--muted">No careflow windows applicable</span>
          )}
        </div>
      </div>

      {/* Check-in + Rounds side by side */}
      <div className="pp-wellness-grid">
        {/* Check-in Windows */}
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">
              Check-in Windows
              <span className={"pp-section__count"}>({checkinWindows?.length})</span>
            </h3>
          </div>
          <div className="pp-section__body">
            {checkinWindows.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {checkinWindows.map((w, idx) => (
                  <div key={`ci-${idx}`} className="rpp-careflow-item">
                    <span className="rpp-careflow-name" title={w.description}>
                      {w.description}
                    </span>
                    <span className="rpp-careflow-time">
                      {formatTime(w.window_start)} &ndash; {formatTime(w.window_end)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="dp-cell-text dp-cell-text--muted">No check-in windows</span>
            )}
          </div>
        </div>

        {/* Rounds Windows */}
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">
              Rounds Windows
              <span className={"pp-section__count"}>({roundsWindows?.length})</span>
            </h3>
          </div>
          <div className="pp-section__body">
            {roundsWindows.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {roundsWindows.map((w, idx) => (
                  <div key={`rnd-${idx}`} className="rpp-careflow-item">
                    <span className="rpp-careflow-name" title={w.careflow_description || w.description}>
                      {w.careflow_description || w.description}
                    </span>
                    <span className="rpp-careflow-time">
                      {formatTime(w.window_start)} &ndash; {formatTime(w.window_end)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="dp-cell-text dp-cell-text--muted">No rounds windows</span>
            )}
          </div>
        </div>
      </div>

      {/* EHS Provider info */}
      {resWellData?.ehs_provider ? (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">EHS Provider</h3>
          </div>
          <div className="pp-section__body">
            <div className="pp-field-grid">
              <div className="pp-field">
                <span className="pp-field__label">Provider</span>
                <span className="pp-field__value">{resWellData.ehs_provider}</span>
              </div>
              {resWellData.ehs_url ? (
                <div className="pp-field">
                  <span className="pp-field__label">URL</span>
                  <span className="pp-field__value">
                    <a href={resWellData.ehs_url} target="_blank" rel="noreferrer">
                      {resWellData.ehs_url}
                    </a>
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default WellnessTab;
